import { useMemo, useState } from 'react';

import type { ArInvoiceListRow, CreateArBillingNoteInput } from '@dcc-erp/shared';
import { CreateArBillingNoteSchema, dateToInputValue, fmt, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useArInvoices, useCreateBillingNote, useCustomers } from '../../lib/queries';
import { AppModal } from '../AppModal';
import { SearchSelect } from '../SearchSelect';
import { modalBody, modalError, modalFooter, modalLabel } from './modal-style';

// =============================================================================
// Create Billing Note (ใบวางบิล) — customer-first invoice bundling.
//
// CreateBillingNoteModal (container) owns customer selection + data fetching;
// the inner CreateBillingNoteForm (presentational, router/data-free) owns the
// tickable open-invoice grid + the running outstanding total so the submit
// payload is unit-testable without a query client — exactly the split used by
// RecordReceiptModal/CreateCreditNoteModal. A billing note posts NO journal
// entry; it just bundles which unpaid invoices were billed + when.
// =============================================================================

// Minimal shape the form reads — accept full list rows but only depend on these
// so callers (and tests) can pass lean fixtures.
type OpenInvoice = Pick<
  ArInvoiceListRow,
  'id' | 'voucherNo' | 'invoiceDate' | 'totalAmount' | 'paidAmount' | 'creditedAmount' | 'dueDate'
>;

export interface CreateBillingNoteFormProps {
  openInvoices: OpenInvoice[];
  onSubmit: (payload: CreateArBillingNoteInput) => void;
  submitting: boolean;
  // Container-supplied; the customer is already chosen upstream. Defaults to ''
  // so the presentational form can be rendered in isolation (tests assert only
  // the submitted payload shape).
  customerId?: string;
}

export function CreateBillingNoteForm({
  openInvoices,
  onSubmit,
  submitting,
  customerId = '',
}: CreateBillingNoteFormProps) {
  const today = new Date();
  const [billingDate, setBillingDate] = useState(dateToInputValue(today));
  const [dueDate, setDueDate] = useState(dateToInputValue(today));
  const [note, setNote] = useState('');
  // Per-invoice tick. A billing note bills the WHOLE current outstanding of each
  // ticked invoice (no partial — partial collection is the receipt's job), so we
  // only track the tick set; the snapshot amount is computed server-side.
  const [ticked, setTicked] = useState<Record<string, boolean>>({});

  const outstandingById = useMemo(() => {
    const m: Record<string, number> = {};
    for (const inv of openInvoices) {
      // outstanding = total − paid − credited (Phase 2b).
      m[inv.id] =
        toNumber(inv.totalAmount) - toNumber(inv.paidAmount) - toNumber(inv.creditedAmount);
    }
    return m;
  }, [openInvoices]);

  function toggle(invId: string) {
    setTicked((prev) => ({ ...prev, [invId]: !prev[invId] }));
  }

  // Ticked invoice ids (only those still in the open list).
  const invoiceIds = useMemo(
    () => openInvoices.filter((inv) => ticked[inv.id]).map((inv) => inv.id),
    [openInvoices, ticked],
  );

  // Running total of the ticked invoices' live outstanding.
  const total = useMemo(
    () => invoiceIds.reduce((sum, id) => sum + (outstandingById[id] ?? 0), 0),
    [invoiceIds, outstandingById],
  );

  const disabled = invoiceIds.length === 0 || !billingDate || !dueDate || submitting;

  function handleSubmit() {
    const payload: CreateArBillingNoteInput = {
      customerId,
      invoiceIds,
      billingDate,
      dueDate,
      ...(note ? { note } : {}),
    };
    onSubmit(payload);
  }

  return (
    <div style={modalBody}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่วางบิล</label>
          <input
            type="date"
            className="input"
            aria-label="billing-date"
            value={billingDate}
            onChange={(e) => setBillingDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>วันนัดรับเช็ค</label>
          <input
            type="date"
            className="input"
            aria-label="due-date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
      </div>

      <div>
        <label style={modalLabel}>ใบแจ้งหนี้คงค้าง</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th>เลขที่ใบแจ้งหนี้</th>
                <th>ลงวันที่</th>
                <th>ครบกำหนด</th>
                <th className="num">ยอดค้าง</th>
              </tr>
            </thead>
            <tbody>
              {openInvoices.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 16 }}>
                    ไม่พบใบแจ้งหนี้คงค้าง
                  </td>
                </tr>
              )}
              {openInvoices.map((inv) => {
                const out = outstandingById[inv.id] ?? 0;
                const on = !!ticked[inv.id];
                return (
                  <tr key={inv.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`select-${inv.voucherNo}`}
                        checked={on}
                        onChange={() => toggle(inv.id)}
                      />
                    </td>
                    <td className="code">{inv.voucherNo}</td>
                    <td>{String(inv.invoiceDate).slice(0, 10)}</td>
                    <td>{String(inv.dueDate).slice(0, 10)}</td>
                    <td className="num tnum">{fmt(out, 2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <label style={modalLabel}>หมายเหตุ (optional)</label>
        <input
          className="input"
          aria-label="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="หมายเหตุ / เงื่อนไขการรับวางบิล"
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--brand-tint)',
          fontSize: 14,
          gap: 16,
        }}
      >
        <span className="muted">เลือกแล้ว {invoiceIds.length} ใบ</span>
        <span>
          ยอดรวมที่วางบิล{' '}
          <b className="tnum" style={{ fontSize: 16 }}>
            {fmt(total, 2)} บาท
          </b>
        </span>
      </div>

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'สร้างใบวางบิล'}
        </button>
      </div>
    </div>
  );
}

export interface CreateBillingNoteModalProps {
  companyId: string;
  // `| undefined` (not just optional) so callers may pass an explicit undefined
  // under exactOptionalPropertyTypes.
  presetCustomerId?: string | undefined;
  onClose: () => void;
}

export function CreateBillingNoteModal({
  companyId,
  presetCustomerId,
  onClose,
}: CreateBillingNoteModalProps) {
  const [customerId, setCustomerId] = useState<string>(presetCustomerId ?? '');

  const { data: customers = [] } = useCustomers(companyId, true);
  const create = useCreateBillingNote(companyId);

  // Open invoices for the chosen customer — POSTED only, then trimmed to rows
  // that still carry outstanding (a POSTED row can be fully paid pre-PAID flip).
  const { data: invoices = [] } = useArInvoices(
    companyId,
    customerId ? { customerId, status: 'POSTED' } : {},
  );
  const openInvoices = useMemo(
    () =>
      customerId
        ? invoices.filter(
            (r) =>
              toNumber(r.totalAmount) - toNumber(r.paidAmount) - toNumber(r.creditedAmount) > 0,
          )
        : [],
    [invoices, customerId],
  );

  const customerLabel = useMemo(() => {
    const m = new Map(customers.map((c) => [c.id, `${c.code} · ${c.nameTh}`]));
    return (id: string) => m.get(id) ?? id;
  }, [customers]);

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: `${c.code} · ${c.nameTh}` })),
    [customers],
  );

  async function handleSubmit(payload: CreateArBillingNoteInput) {
    // Validate the wire shape (≥1 invoice, date format) before the POST.
    const data = CreateArBillingNoteSchema.parse({ ...payload, customerId });
    await create.mutateAsync(data);
    onClose();
  }

  // WS8 R2 — standard AppModal (1200px) replaces the bespoke 720px chrome.
  // Container-only: CreateBillingNoteForm keeps its body + in-body submit button.
  return (
    <AppModal title="สร้างใบวางบิล" onClose={onClose}>
      <div style={{ padding: '0 22px 0' }}>
        <label style={modalLabel}>ลูกค้า</label>
        <SearchSelect
          value={customerId}
          onChange={setCustomerId}
          options={customerOptions}
          getLabel={customerLabel}
          placeholder="— เลือกลูกค้า —"
          disabled={!!presetCustomerId}
        />
      </div>

      {customerId ? (
        <CreateBillingNoteForm
          key={customerId}
          openInvoices={openInvoices}
          onSubmit={(payload) => void handleSubmit(payload)}
          submitting={create.isPending}
          customerId={customerId}
        />
      ) : (
        <div style={{ ...modalBody, color: 'var(--text-muted)' }}>
          เลือกลูกค้าเพื่อแสดงใบแจ้งหนี้คงค้าง
        </div>
      )}

      {create.error && (
        <div style={{ ...modalError, padding: '0 22px 16px' }}>
          บันทึกไม่สำเร็จ: {create.error.message}
        </div>
      )}
    </AppModal>
  );
}
